import React, { useState } from "react";
import { safeUrl } from "../../../utils/safeUrl";
import { FiX, FiSearch, FiGlobe, FiRefreshCw, FiExternalLink, FiAlertTriangle } from "react-icons/fi";
import { discoverSitePages } from "../../../utils/taskApi";

/**
 * Pick the page a website task points at.
 *
 * An admin knows the site; they do not usually have the URL of the one page
 * they want, so the alternative to this is alt-tabbing to a browser and
 * copying an address out of the URL bar. Here they type the domain once and
 * choose from a list.
 *
 * The list is not guaranteed to be complete, and the modal says which of the
 * two sources produced it — a sitemap, which is the site's own published
 * index, or the homepage's links, which finds the main sections and nothing
 * deeper. Presenting a four-item fallback as "this site has four pages" would
 * be the one genuinely misleading thing this screen could do.
 */

const PagePickerModal = ({ isOpen, onClose, onPick, initialUrl = "" }) => {
  const [site, setSite] = useState(initialUrl);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  if (!isOpen) return null;

  const search = async () => {
    if (!site.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await discoverSitePages(site.trim());
      const data = res && res.data;

      if (res && res.status === false) {
        setError(res.message);
      } else if (!data || !Array.isArray(data.pages)) {
        // The call succeeded but the body is not the shape this screen expects
        // — a stale backend, or something in front of it answering instead.
        // Say that, rather than crashing on `data.total` and showing the
        // reader a raw JavaScript error.
        setError(
          "The server replied, but not with a page list. The backend may be running an older build that does not have this endpoint yet."
        );
      } else {
        setResult(data);
        // A successful call that found nothing still carries its explanation
        // in the message — worth showing, since an empty list otherwise looks
        // like a failure.
        if (!data.total) setError(res.message);
      }
    } catch (err) {
      setError(err.message || "Could not read that site");
    } finally {
      setLoading(false);
    }
  };

  const pages = (result?.pages || []).filter((page) => {
    if (!filter.trim()) return true;
    const needle = filter.trim().toLowerCase();
    return page.label.toLowerCase().includes(needle) || page.path.toLowerCase().includes(needle);
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <FiGlobe size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Pick a page</h2>
              <p className="text-[11px] text-gray-400">
                Type the website once, then choose the page this task should open.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition">
            <FiX size={17} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={site}
              onChange={(e) => setSite(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  search();
                }
              }}
              placeholder="example.com"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            <button
              type="button"
              onClick={search}
              disabled={loading || !site.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50 flex items-center gap-2 shrink-0"
            >
              {loading ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSearch size={14} />}
              {loading ? "Reading..." : "Find pages"}
            </button>
          </div>

          {result && result.total > 0 && (
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Filter ${result.total} pages...`}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="py-12 text-center text-gray-400 text-xs flex flex-col items-center gap-2">
              <FiRefreshCw className="animate-spin text-blue-600" size={20} />
              Looking for this site's pages...
            </div>
          )}

          {!loading && error && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={15} />
              <p className="text-xs text-amber-800">{error}</p>
            </div>
          )}

          {!loading && !error && !result && (
            <div className="py-12 text-center">
              <FiGlobe size={26} className="text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500 font-medium">Enter a website address above</p>
              <p className="text-[11px] text-gray-400 mt-1">
                Its published sitemap is read first, so most sites list every page.
              </p>
            </div>
          )}

          {!loading && result && result.total > 0 && (
            <>
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[11px] text-gray-400">
                  {pages.length === result.total
                    ? `${result.total} pages`
                    : `${pages.length} of ${result.total} pages`}
                  {result.source === "sitemap" ? " · from the site's sitemap" : " · from the homepage's links"}
                </p>
              </div>

              {result.note && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2.5">
                  {result.note}
                </p>
              )}

              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                {pages.length === 0 ? (
                  <p className="px-4 py-8 text-center text-xs text-gray-400">Nothing matches that filter.</p>
                ) : (
                  pages.map((page) => (
                    <div key={page.url} className="flex items-center gap-2 hover:bg-gray-50 transition">
                      <button
                        type="button"
                        onClick={() => {
                          onPick(page.url);
                          onClose();
                        }}
                        className="flex-1 text-left px-4 py-2.5 min-w-0"
                      >
                        <p className="text-xs font-semibold text-gray-800 truncate">{page.label}</p>
                        <p className="text-[11px] text-gray-400 truncate">{page.path}</p>
                      </button>
                      <a
                        href={safeUrl(page.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in a new tab to check it"
                        className="p-2 mr-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition shrink-0"
                      >
                        <FiExternalLink size={14} />
                      </a>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagePickerModal;
